import { useEffect, useRef, useState } from "react";
import { effieOnBoardingSections } from "./data";
import { effieOnboardStyles } from "./style/style";
import { VisualRenderer } from "./VisualRenderer";

const EffieOnboarding = () => {
  const [activeStep, setActiveStep] = useState(0);
  const sectionRefs = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveStep(Number(entry.target.getAttribute("data-index")));
          }
        });
      },
      { threshold: 0.4, rootMargin: "-10% 0px -40% 0px" }
    );

    sectionRefs.current.forEach((el) => {
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  return (
    <div className="bg-[#0B0C10] min-h-screen text-slate-300 font-sans selection:bg-[#45A29E] selection:text-white">
      {/* --- CSS ANIMATIONS --- */}
      <style>{effieOnboardStyles}</style>

      <div className="max-w-7xl mx-auto flex flex-col lg:flex-row">
        {/* === LEFT COLUMN: NARRATIVE STREAM === */}
        <div className="w-full lg:w-1/2 px-6 py-20 lg:py-32 flex flex-col gap-24 lg:gap-48 z-10">
          <div className="mb-8">
            <h1 className="text-3xl font-black tracking-tighter bg-clip-text text-transparent bg-gradient-to-r from-white to-slate-500">
              <span className="text-[#ff00ff]">Effie</span>
            </h1>
            <p className="text-lg text-slate-400 border-l-2 border-[#45A29E] pl-4">
              Your Companion Onboarding Guide
            </p>
          </div>

          {effieOnBoardingSections.map((section, index) => (
            <div
              key={section.id}
              data-index={index}
              ref={(el) => (sectionRefs.current[index] = el)}
              className={`transition-all duration-700 ${
                activeStep === index
                  ? "opacity-100 translate-x-0"
                  : "opacity-40 translate-x-0"
              }`}
            >
              <div className="flex items-center gap-3 mb-6">
                <span
                  className={`font-mono text-sm px-2 py-1 rounded ${
                    activeStep === index
                      ? "bg-[#45A29E] text-black"
                      : "bg-slate-800 text-slate-500"
                  }`}
                >
                  0{index + 1}
                </span>
                <span className="text-[#45A29E] font-mono text-xs uppercase tracking-widest">
                  {section.subtitle}
                </span>
              </div>

              <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
                {section.title}
              </h2>

              <div className="text-lg leading-relaxed text-slate-400 prose prose-invert">
                {section.content}
              </div>

              {/* MOBILE VISUAL (Shows only on small screens) */}
              <div className="lg:hidden mt-8 h-[300px] w-full bg-[#1F2833] rounded-2xl overflow-hidden relative border border-white/10">
                <VisualRenderer visual={section.visual} isActive={true} />
              </div>
            </div>
          ))}

          <div className="h-[20vh]"></div>
        </div>

        {/* === RIGHT COLUMN: STICKY DIGITAL TWIN (Desktop Only) === */}
        <div className="hidden lg:flex w-1/2 h-screen sticky top-0 items-center justify-center p-12 z-20">
          {/* THE PHONE FRAME */}
          <div className="relative w-[380px] h-[750px] bg-[#1F2833] rounded-[3rem] border-[8px] border-[#0B0C10] shadow-[0_0_50px_rgba(0,0,0,0.5)] overflow-hidden ring-1 ring-white/10 transition-all duration-500">
            {/* Screen Header */}
            <div className="absolute top-0 left-0 w-full h-24 bg-gradient-to-b from-[#0B0C10]/90 to-transparent z-50 flex flex-col items-center justify-start pt-6">
              <div className="w-20 h-6 bg-[#0B0C10] rounded-full flex items-center justify-center gap-2 mb-2">
                <div className="w-1.5 h-1.5 rounded-full bg-[#45A29E] animate-pulse"></div>
                <div className="w-1.5 h-1.5 rounded-full bg-[#45A29E]/30"></div>
              </div>
              <div className="text-[10px] font-mono text-slate-500 uppercase tracking-widest">
                Effie_OS <span className="text-[#45A29E]">Active</span>
              </div>
            </div>

            {/* DYNAMIC SCREEN CONTENT */}
            <div className="w-full h-full bg-[#0B0C10] relative">
              {effieOnBoardingSections.map((section, index) => (
                <div
                  key={section.id}
                  className={`absolute inset-0 transition-all duration-700 ease-in-out ${
                    activeStep === index
                      ? "opacity-100 scale-100 blur-0"
                      : "opacity-0 scale-90 blur-xl pointer-events-none"
                  }`}
                >
                  <VisualRenderer
                    visual={section.visual}
                    isActive={activeStep === index}
                  />
                </div>
              ))}
            </div>

            {/* Screen Footer (Home Indicator) */}
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 w-32 h-1 bg-white/20 rounded-full z-50"></div>

            {/* Gloss Reflection */}
            <div className="absolute inset-0 bg-gradient-to-tr from-white/5 via-transparent to-transparent pointer-events-none rounded-[2.5rem] z-50"></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EffieOnboarding;
